'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import { ANALYTICS_BOTS_CHATS, ANALYTICS_BOTS_MODULES, EMAIL, SEQUENCE_SENDER_SCORE, SOCIAL_CHANNELS, UNIFIED_ADS_ACCOUNTS, UNIFIED_ADS_CAMPAIGNS, UNIFIED_ADS_NETWORKS, UNIFIED_FEEDBACK_EMAIL, UNIFIED_FEEDBACK_TRENDS, UNIFIED_FEEDBACK_WEB } from 'reporting-data/constants/dataTypes';
import * as inboundDbDataTypes from 'reporting-data/constants/dataTypes/inboundDb';
import { get as isUnifiedDataType } from 'reporting-data/retrieve/unified/supported';
import { isMultiConfigReport, isUnifiedEventsReport, isUnifiedEventsQLReport } from 'reporting-data/tsTypes/reportTypes';
import { isImmutableJourneyReport } from 'reporting-snowflake/funnel/types/journeyReport';
import useCheckCustomReportLimitExceededAndReportShouldLockdown from '../../access/limits/hooks/useCheckCustomReportLimitExceededAndReportShouldLockdown';
import useCustomReportLimit from '../../access/limits/hooks/useCustomReportLimit';
import { useHasExportScopeAccess } from '../../access/scopes/hooks/useHasScope';
import { DATA_LAYER, getCustomWidgetType, getReportDataType, isInboundDbReport, isSalesAnalyticsWidget, isValidUnifiedReport, isExternalDataTypeReport } from '../../data/schemas/reportSchema';
import { hasSufficientReportViewPermission, isSnowflake } from '../../lib/report-logic';
import { CUSTOM_REPORT_LIMIT_EXCEEDED, INSUFFICIENT_ASSET_PERMISSION_LEVEL, LACKS_EXPORT_ACCESS, UNSUPPORTED_REPORT_TYPE, LACKS_FIELD_ACCESS } from '../problems';
import { useCustomReportCount } from '../../data/hooks/useReportCounts';
import { isCustom } from '../../lib/report-logic';
import { getRestrictedFieldLevelProperties } from '../../data/schemas/reportSchema';
import RestrictedFieldsProblemMessage from '../shared/RestrictedFieldsProblemMessage';
import { jsx as _jsx } from "react/jsx-runtime";
const isSavedReport = report => !!report.get('id');
const isDataLayerCustomWidgetType = report => getCustomWidgetType(report) === DATA_LAYER;
const isReportBlocklistedDataType = report => {
  const dataType = getReportDataType(report);
  return [UNIFIED_ADS_ACCOUNTS, UNIFIED_ADS_CAMPAIGNS, UNIFIED_ADS_NETWORKS, UNIFIED_FEEDBACK_TRENDS, UNIFIED_FEEDBACK_WEB, UNIFIED_FEEDBACK_EMAIL, SEQUENCE_SENDER_SCORE, SOCIAL_CHANNELS, ANALYTICS_BOTS_CHATS, ANALYTICS_BOTS_MODULES, inboundDbDataTypes.SOCIAL_POSTS].includes(dataType);
};
const isReportEmailDataType = report => getReportDataType(report) === EMAIL;

// Supported report types for export:
//  - Sales Analytics reports
//  - Custom widgets w/ DATA_LAYER as their type
//  - Not blocklisted data type reports
//  - Inbound DB reports
//  - Unified reports (barring those with 'people' as a dimension)
//  - Email DataType reports
//  - Snowflake reports
const isReportTypeSupportedForExport = report => {
  // TODO: The sales analytics report check needs to be implemented. The
  // function used to check this (isSalesAnalyticsWidget) can be found
  // in reporting-ui-components, but this causes a circular dependency
  // unfortunately. Consider lifting this into another library.
  if (isSalesAnalyticsWidget(report)) {
    return true;
  } else if (!isDataLayerCustomWidgetType(report) || isReportBlocklistedDataType(report)) {
    return false;
  } else if (isInboundDbReport(report) || isMultiConfigReport(report)) {
    return true;
  } else if (isUnifiedDataType(report.getIn(['config', 'dataType']))) {
    return isValidUnifiedReport(report);
  } else if (isReportEmailDataType(report) || isSnowflake(report) || isImmutableJourneyReport(report) || isUnifiedEventsReport(report) || isUnifiedEventsQLReport(report) || isExternalDataTypeReport(report)) {
    return true;
  }
  return false;
};
const isLimitedReportTypeSupportedForExport = report => {
  if (isSalesAnalyticsWidget(report)) {
    return false;
  }
  return isImmutableJourneyReport(report) || isUnifiedEventsReport(report) || isExternalDataTypeReport(report) || isInboundDbReport(report);
};
const isSupportedReportType = ({
  report,
  hasLimitedReportTypeSupport
}) => hasLimitedReportTypeSupport ? isLimitedReportTypeSupportedForExport(report) : isReportTypeSupportedForExport(report);
const useExportReportProblem = ({
  report,
  hasLimitedReportTypeSupport
}) => {
  const checkCustomReportLimitExceededAndReportShouldLockdown = useCheckCustomReportLimitExceededAndReportShouldLockdown({
    report
  });
  const customReportLimit = useCustomReportLimit();
  const hasExportScope = useHasExportScopeAccess();
  const restrictedFieldLevelProperties = getRestrictedFieldLevelProperties(report);
  if (checkCustomReportLimitExceededAndReportShouldLockdown) {
    return {
      problem: CUSTOM_REPORT_LIMIT_EXCEEDED,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.exportReport.button.tooltip.customReportLimitExceeded",
        options: {
          limit: customReportLimit
        }
      })
    };
  } else if (isSavedReport(report) && !hasSufficientReportViewPermission(report)) {
    return {
      problem: INSUFFICIENT_ASSET_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.exportReport.button.tooltip.needView"
      })
    };
  } else if (!hasExportScope) {
    return {
      problem: LACKS_EXPORT_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.exportReport.button.tooltip.needExportAccess"
      })
    };
  } else if (!isSupportedReportType({
    report,
    hasLimitedReportTypeSupport
  })) {
    return {
      problem: UNSUPPORTED_REPORT_TYPE,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.exportReport.button.tooltip.reportTypeNotSupported"
      })
    };
  } else if (restrictedFieldLevelProperties.length) {
    return {
      problem: LACKS_FIELD_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(RestrictedFieldsProblemMessage, {
        message: 'reporting-action-components.actions.editReport.button.tooltip.restrictedFieldLevelPermission',
        restrictedFieldLevelProperties: restrictedFieldLevelProperties
      })
    };
  }
  return {
    problem: null,
    disabledMessage: null
  };
};
export const useHasExportDashboardProblem = ({
  reports = [],
  hasLimitedReportTypeSupport
}) => {
  const customReportCount = useCustomReportCount();
  const customReportLimit = useCustomReportLimit();
  const hasExportScope = useHasExportScopeAccess();
  if (!hasExportScope) {
    return true;
  }
  return reports.some(report => {
    if (isCustom(report) && customReportCount > customReportLimit) {
      return true;
    }
    if (isSavedReport(report) && !hasSufficientReportViewPermission(report)) {
      return true;
    }
    if (!isSupportedReportType({
      report,
      hasLimitedReportTypeSupport
    })) {
      return true;
    }
    return false;
  });
};
export default useExportReportProblem;